import React from 'react';
import styles from '../styles/step1WritingModal.module.css';
import type { Step1ArchiveSummary } from '../services/step1WritingService';
import { payloadHasMeaningfulStep1Content, readStep1LocalBackup } from '../services/step1WritingService';
import { useLocale } from '../context/LocaleContext';
import { formatWordCountLabel } from '../utils/readTimeEstimate';

interface Step1RecoverOverlayProps {
  userBackupKey: string;
  archiveSummaries: Step1ArchiveSummary[];
  isArchivesLoading: boolean;
  onClose: () => void;
  onRestoreArchive: (summary: Step1ArchiveSummary) => void;
  onRestoreLocal: () => void;
}

const Step1RecoverOverlay: React.FC<Step1RecoverOverlayProps> = ({
  userBackupKey,
  archiveSummaries,
  isArchivesLoading,
  onClose,
  onRestoreArchive,
  onRestoreLocal,
}) => {
  const { t, locale } = useLocale();
  const local = readStep1LocalBackup(userBackupKey);

  return (
    <div className={styles.recoverOverlay} onClick={onClose}>
      <div
        className={styles.recoverModal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="s1-recover-title"
        aria-modal="true"
      >
        <div className={styles.recoverHeader}>
          <h2 id="s1-recover-title">{t('step.1.recover.title')}</h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={t('step.1.writing.close')}
          >
            ×
          </button>
        </div>
        <div className={styles.recoverBody}>
          <p className={styles.recoverIntro}>{t('step.1.recover.intro')}</p>
          {local && payloadHasMeaningfulStep1Content(local.payload) && (
            <div className={styles.recoverSection}>
              <h3 className={styles.recoverSectionTitle}>{t('step.1.recover.local_heading')}</h3>
              <button type="button" className={styles.btn} onClick={onRestoreLocal}>
                {t('step.1.recover.use_local', {
                  date: new Date(local.savedAt).toLocaleString(locale, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  }),
                })}
              </button>
            </div>
          )}
          <div className={styles.recoverSection}>
            <h3 className={styles.recoverSectionTitle}>{t('step.1.recover.server_heading')}</h3>
            {isArchivesLoading && (
              <p className={styles.recoverLoading}>{t('step.1.recover.loading')}</p>
            )}
            {!isArchivesLoading && archiveSummaries.length === 0 && (
              <p className={styles.recoverEmpty}>{t('step.1.recover.empty')}</p>
            )}
            {!isArchivesLoading && archiveSummaries.length > 0 && (
              <ul className={styles.recoverList}>
                {archiveSummaries.map((summary) => (
                  <li key={summary.version} className={styles.recoverListItem}>
                    <div className={styles.recoverListMeta}>
                      <strong>{summary.label}</strong>
                      <span>{formatWordCountLabel(summary.wordCount)}</span>
                    </div>
                    <button
                      type="button"
                      className={styles.btnPrimary}
                      onClick={() => onRestoreArchive(summary)}
                    >
                      {t('step.1.recover.use_this')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className={styles.recoverFooter}>
          <button type="button" className={styles.btn} onClick={onClose}>
            {t('step.1.writing.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step1RecoverOverlay;
